import React, { ReactElement } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';

import {
  gray40,
  gray80,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { boldMd, regularLg, regularXl } from '../../../constants/location';
import {
  OCR_CREDIT_ADD_CONFIRM_DIALOG_TITLE,
  OCR_CREDIT_ADD_CONFIRM_DIALOG_CANCEL,
  OCR_CREDIT_ADD_CONFIRM_DIALOG_SUBMIT,
} from '../../../constants/labels';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  amount: number;
  expiresAt: Date;
  loading?: boolean;
};

const styles = {
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  confirmButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  confirmButtonText: {
    ...boldMd,
    color: 'white',
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      '@media screen and (max-width: 960px)': {
        padding: '32px 16px',
      },
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  icon: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  iconButton: {
    padding: 0,
  },
  title: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  message: {
    whiteSpace: 'pre-line',
  },
};

const OcrCreditAddConfirmModal = ({
  open,
  onClose,
  onConfirm,
  amount,
  expiresAt,
  loading = false,
}: Props): ReactElement => {
  const formatExpiresAt = (date: Date): string => {
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
  };

  const message = `追加金額: ${amount.toLocaleString()}円\n有効期限: ${formatExpiresAt(expiresAt)}\n\n実行してもよろしいですか？`;

  return (
    <Dialog open={open} sx={styles.dialog}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{OCR_CREDIT_ADD_CONFIRM_DIALOG_TITLE}</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>
      <Typography sx={styles.message}>{message}</Typography>
      <Box sx={styles.buttonWrapper}>
        <Button sx={styles.cancelButton} onClick={onClose} disabled={loading}>
          <Typography sx={styles.cancelButtonText}>
            {OCR_CREDIT_ADD_CONFIRM_DIALOG_CANCEL}
          </Typography>
        </Button>
        <Button sx={styles.confirmButton} onClick={onConfirm} disabled={loading}>
          <Typography sx={styles.confirmButtonText}>
            {loading ? '追加中...' : OCR_CREDIT_ADD_CONFIRM_DIALOG_SUBMIT}
          </Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default OcrCreditAddConfirmModal;
